import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import keys from 'lodash/keys';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import values from 'lodash/values';
import { Component } from 'react';
import { Alert } from 'react-bootstrap';
import type { RouteComponentProps } from 'react-router';

import { getNdJsonStream, ndJsonChunkIsError } from '../../../helpers/ndjson';
import { get } from '../../../redux/api';
import FallbackImage from '../../generic/FallbackImage';
import Loader from '../../generic/Loader';
import Panel from '../../generic/Panel';
import ModalDisplayLicense from '../../modals/ModalDisplayLicense';
import LicensePanel from '../generic/LicensePanel';
import type { Obligation } from '../../../../typings/api';
import type { Dictionary } from '../../../../typings/common';
import type { APIError, ProjectGroup, Revision, SourcedLicense } from '../../../types';
import type { MultipleRevisionsDisplayMode } from '../../modals/ModalDisplayLicense';

import { ObligationsPanel } from './components/ObligationsPanel';
import { withSelectedRelease } from './components/withSelectedRelease';
import type { LoadedProjectGroupRelease } from './components/withSelectedRelease';

// This component is forced to rely on index keys often.

export type LicenseTabProps = {
  projectGroup: ProjectGroup;
  release: LoadedProjectGroupRelease;
} & RouteComponentProps<{}, {}>;

export type LicenseTabState = {
  loading: boolean;
  errors: APIError[];
  revisions: Dictionary<Revision>;
  licenses: Dictionary<SourcedLicense>;
  obligations: Dictionary<Obligation[]>;
  selectedObligation?: string;
  selectedLicense?: string;
};

class ErrorAlert extends Component<{ error: APIError; onDismiss: (error: APIError) => void }> {
  onDismiss = () => this.props.onDismiss(this.props.error);

  render() {
    return (
      <Alert bsStyle="error" onDismiss={this.onDismiss}>{this.props.error.message}</Alert>
    );
  }
}

const Errors = (props: { errors: APIError[]; onDismiss: (error: APIError) => void }) => {
  const { errors = [] } = props;
  if (isEmpty(errors)) {
    return null;
  }
  return (
    <div>
      {map(errors, (error, i) => (
        <ErrorAlert key={i} error={error} onDismiss={props.onDismiss} />
      ))}
    </div>
  );
};

function combineProjectLicenses(
  licenses: Dictionary<SourcedLicense>,
  incoming: SourcedLicense,
): Dictionary<SourcedLicense> {
  const { licenseId } = incoming;
  if (!licenses[licenseId]) {
    return {
      ...licenses,
      [licenseId]: incoming,
    };
  }

  // Select the primary license record based on depth (more shallow wins)
  const primary = licenses[licenseId].depth <= incoming.depth ? licenses[licenseId] : incoming;
  const secondary = primary === incoming ? licenses[licenseId] : incoming;

  // Fold secondary into primary
  primary.matches = uniq([...primary.matches, ...secondary.matches]);
  primary.sources = uniq([...(primary.sources ?? []), ...(secondary.sources ?? [])]);
  return {
    ...licenses,
    [licenseId]: primary,
  };
}

function combineObligations(
  destination: Dictionary<Obligation[]>,
  incoming: Dictionary<Obligation[]>,
): Dictionary<Obligation[]> {
  const obligations = { ...destination };
  keys(incoming).forEach(directive => {
    if (!obligations[directive]) {
      obligations[directive] = incoming[directive];
      return;
    }

    const existingDirective = obligations[directive];
    const incomingDirective = incoming[directive];
    incomingDirective.forEach(obligation => {
      const existingObligation = existingDirective.find(existing => existing.license === obligation.license);
      if (existingObligation) {
        // Don't need to check for uniqueness, we only fetch a given locator's todos once.
        keys(obligation.revisions).forEach(locator => {
          existingObligation.revisions[locator] = obligation.revisions[locator];
        });
        return;
      }

      existingDirective.push(obligation);
    });

    obligations[directive] = existingDirective;
  });

  return obligations;
}

class ProjectGroupLicenses extends Component<LicenseTabProps, LicenseTabState> {
  readonly state: LicenseTabState = {
    loading: false,
    revisions: {},
    licenses: {},
    errors: [],
    obligations: {},
  };

  componentDidMount() {
    this.updateComponentData();
  }

  async componentDidUpdate(prevProps: Readonly<LicenseTabProps>) {
    if (this.props.release && this.props.release.data.id !== prevProps.release.data.id) {
      this.updateComponentData();
    }
  }

  onSelectObligation = (selectedObligation: string) => {
    this.setState(state => ({
      selectedObligation: state.selectedObligation === selectedObligation ? null : selectedObligation,
    }));
  };

  onSelectLicense = (selectedLicense: string) => {
    this.setState({ selectedLicense });
  };

  onCloseLicenseModal = () => {
    this.setState({ selectedLicense: null });
  };

  onDismissError = (error: APIError) => {
    this.setState(state => ({
      errors: state.errors.filter(e => e.message !== error.message),
    }));
  };

  fetchRevisions = async () => {
    try {
      const { projectGroup: { id: groupId }, release: { data: { id: releaseId } } } = this.props;
      const revisionsResp = await get<Revision[]>(`/api/project_group/${groupId}/release/${releaseId}/revisions`);
      const revisions = revisionsResp.data;

      this.setState({ revisions: keyBy(revisions, 'locator') });
    } catch (error) {
      this.setState(state => ({ errors: [...state.errors, error] }));
    }
  };

  fetchLicensesStream = async () => {
    const { projectGroup: { id: groupId }, release: { data: { id: releaseId } } } = this.props;
    const stream = await getNdJsonStream<SourcedLicense>(`/api/project_group/${groupId}/release/${releaseId}/licenses`);
    const reader = stream.getReader();
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { value: license, done } = await reader.read();
      if (done) {
        break;
      }

      if (ndJsonChunkIsError(license)) {
        this.setState(state => ({ errors: [...state.errors, license.error] }));
      } else {
        this.setState(({ licenses }) => ({ licenses: combineProjectLicenses(licenses, license) }));
      }
    }
  };

  fetchLicenses = async () => {
    try {
      await this.fetchLicensesStream();
      return;
    } catch {
      // could not fetch streaming; browser probably doesn't support it. Fallback.
    }

    try {
      const { projectGroup: { id: groupId }, release: { data: { id: releaseId } } } = this.props;
      const licensesResp = await get<Dictionary<SourcedLicense>>(`/api/project_group/${groupId}/release/${releaseId}/licenses`);
      const licenses = licensesResp.data;

      this.setState({ licenses });
    } catch (error) {
      this.setState(state => ({ errors: [...state.errors, error] }));
    }
  };

  fetchObligationsStream = async () => {
    const { projectGroup: { id: groupId }, release: { data: { id: releaseId } } } = this.props;
    const stream = await getNdJsonStream<Dictionary<Obligation[]>>(`/api/project_group/${groupId}/release/${releaseId}/obligations`);
    const reader = stream.getReader();
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { value: incoming, done } = await reader.read();
      if (done) {
        break;
      }

      if (ndJsonChunkIsError(incoming)) {
        this.setState(state => ({ errors: [...state.errors, incoming.error] }));
      } else {
        this.setState(({ obligations }) => ({ obligations: combineObligations(obligations, incoming) }));
      }
    }
  };

  fetchObligations = async () => {
    try {
      await this.fetchObligationsStream();
      return;
    } catch {
      // could not fetch streaming; browser probably doesn't support it. Fallback.
    }

    try {
      const { projectGroup: { id: groupId }, release: { data: { id: releaseId } } } = this.props;
      const obligationsResp = await get<Dictionary<Obligation[]>>(`/api/project_group/${groupId}/release/${releaseId}/obligations`);
      const obligations = obligationsResp.data;

      this.setState({ obligations });
    } catch (error) {
      this.setState(state => ({ errors: [...state.errors, error] }));
    }
  };

  updateComponentData = async () => {
    const { projectGroup, release } = this.props;
    if (!has(projectGroup, 'id') || !has(release, 'data.id')) {
      return;
    }

    this.setState({ loading: true, licenses: {}, obligations: {}, revisions: {} });
    try {
      await Promise.all([
        this.fetchRevisions(),
        this.fetchLicenses(),
        this.fetchObligations(),
      ]);
    } catch (error) {
      this.setState(state => ({ errors: [...state.errors, error] }));
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { licenses, selectedLicense: selectedLicenseId } = this.state;
    const hasLicenses = !isEmpty(licenses);
    const hasRevisions = !isEmpty(this.state.revisions);
    const hasObligations = !!keys(this.state.obligations).length;

    /* The code in this component is written in such a way that we could
      display the licenses as they "stream" from the server (we look up licenses for each locator seperately)
      but none of the other pages in the app do this, so we'll just block until they're all available. */
    if (this.props.release?.loading) {
      return (
        <div className="margin-top">
          <Loader />
        </div>
      );
    }

    const selectedLicense: SourcedLicense | undefined = selectedLicenseId
      ? licenses[selectedLicenseId]
      : undefined;

    let licenseDisplayMode: MultipleRevisionsDisplayMode | null = null;
    if (selectedLicense) {
      licenseDisplayMode = {
        mode: 'multiple',
        licenseId: selectedLicense.licenseId,
        matchRevisionIds: selectedLicense.matches?.sort() || [],
      };
    }

    return (
      <div className="container container-responsive">
        <div className="margin-top">
          <div className="animated fadeInDown quick">
            <Errors errors={this.state.errors} onDismiss={this.onDismissError} />
            {this.state.loading && <Loader />}
            <div className={hasObligations ? 'col-md-7' : 'col-md-12'}>
              {hasLicenses && hasRevisions ? (
                <LicensePanel
                  loading={this.state.loading}
                  licenses={values(this.state.licenses)}
                  revisions={this.state.revisions}
                  onSelectLicense={this.onSelectLicense}
                />
              ) : (
                // If we don't have licenses or revisions, we want to show a placeholder panel
                // indicating that no license data is available.
                !this.state.loading && (
                  <Panel>
                    <FallbackImage src="no-results" alt="No results" hasPadding>
                      <h3>No license data available</h3>
                      <p>
                        License data is not available for this release. This may be because the release has not been
                        analyzed, or because the release does not contain any software components with licenses.
                      </p>
                    </FallbackImage>
                  </Panel>
                )
              )}
            </div>
            {hasObligations && hasLicenses && hasRevisions && (
              <div className="col-md-5">
                <div className="todo-main">
                  <ObligationsPanel
                    revisions={keys(this.state.revisions).sort()}
                    obligations={this.state.obligations}
                    selectedLicense={this.state.selectedLicense}
                    onSelectLicense={this.onSelectLicense}
                    onSelectObligation={this.onSelectObligation}
                    selectedObligation={this.state.selectedObligation}
                  />
                </div>
              </div>
            )}
          </div>
          <ModalDisplayLicense displayMode={licenseDisplayMode} onHide={this.onCloseLicenseModal} />
        </div>
      </div>
    );
  }
}

export default withSelectedRelease(ProjectGroupLicenses);
